import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ORDO_NODES, ORDO_READING, ORDO_STATEMENT, RESPONSIBILITY_IDS } from "@/content/ordo";
import { PageIntro } from "@/components/site/PageIntro";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { ActionAnchor, ActionLink, Container, Eyebrow, Mono, SectionTitle } from "@/components/site/primitives";
import { ArchitectureExplorer } from "@/components/explorers/ArchitectureExplorer";

const Whole = () => (
  <section className="border-b border-o-line" data-testid="ordo-whole">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <Eyebrow index="01">The architecture as a whole</Eyebrow>
          <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">Five responsibilities, held together.</SectionTitle>
          <ol className="mt-8 border-t border-o-line">
            {RESPONSIBILITY_IDS.map((id) => (
              <li key={id} className="flex items-baseline gap-4 border-b border-o-line py-3">
                <Mono accent>{ORDO_NODES[id].index}</Mono>
                <span className="font-display font-bold">{ORDO_NODES[id].label}</span>
                <span className="ml-auto text-right text-xs text-o-dim">{ORDO_NODES[id].tagline}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="space-y-6 text-base leading-[1.8] text-o-dim sm:text-[17px] lg:col-span-7 lg:col-start-6">
        {ORDO_READING.map((p, i) => (
          <p key={i} className={i === 0 ? "font-display text-xl font-medium leading-snug text-o-text sm:text-2xl" : ""}>{p}</p>
        ))}
        <div className="border border-dashed border-o-strong p-5">
          <Mono>Reserved for Phase 3</Mono>
          <p className="mt-2 text-sm">Extended technical explanation of the architecture: space reserved for approved long-form content.</p>
        </div>
      </div>
    </Container>
  </section>
);

const EXPRESSIONS = [["infrastructure", "Separate licensing route"], ["engine", "Ordo software family"], ["stewardship", "Ordo software family"]];

const Expressions = () => (
  <section id="expressions" className="border-b border-o-line" data-testid="ordo-expressions">
    <Container className="py-16 lg:py-24">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow index="03">Expressions</Eyebrow>
          <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">Where the architecture is expressed.</SectionTitle>
        </div>
        <p className="text-o-dim lg:col-span-4 lg:col-start-9">Direct routes to every destination described in the explorer.</p>
      </div>
      <ul className="mt-10 border-t border-o-line">
        {EXPRESSIONS.map(([id, kind]) => {
          const n = ORDO_NODES[id];
          return (
            <li key={id}>
              <Link to={n.to} data-testid={`ordo-expression-${id}`}
                className="group grid gap-2 border-b border-o-line py-6 transition-colors hover:bg-o-surface sm:grid-cols-12 sm:items-baseline sm:gap-6 sm:px-3">
                <Mono accent className="sm:col-span-3">{kind}</Mono>
                <span className="font-display text-2xl font-bold sm:col-span-3">{n.label}</span>
                <span className="text-o-dim sm:col-span-5">{n.concerns}</span>
                <ArrowUpRight className="hidden h-5 w-5 justify-self-end text-o-dim transition-colors group-hover:text-o-accent sm:col-span-1 sm:block" />
              </Link>
            </li>
          );
        })}
      </ul>
      <div className="mt-12">
        <Mono>Related applied capabilities · separate offerings, not subdivisions of General Ordo</Mono>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {["breakpoint", "mesh"].map((id) => (
            <Link key={id} to={ORDO_NODES[id].to} data-testid={`ordo-related-${id}`}
              className="group flex items-start justify-between gap-4 border border-dashed border-o-strong p-5 transition-colors hover:border-o-accent">
              <span>
                <span className="block font-display text-xl font-bold">{ORDO_NODES[id].label}</span>
                <span className="mt-1 block text-sm text-o-dim">{ORDO_NODES[id].tagline} · {ORDO_NODES[id].concerns}</span>
              </span>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-o-dim group-hover:text-o-accent" />
            </Link>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

const Disclosure = () => (
  <section data-testid="ordo-disclosure">
    <Container className="grid gap-4 py-16 md:grid-cols-2 lg:py-20">
      <div className="border border-o-line bg-o-surface p-6 sm:p-8">
        <Mono accent>Described on this site</Mono>
        <ul className="mt-4 space-y-2 text-[15px]">
          <li>Governing responsibilities and how they relate</li>
          <li>Where the architecture is expressed</li>
          <li>How to pursue deeper examination</li>
        </ul>
      </div>
      <div className="border border-dashed border-o-strong p-6 sm:p-8">
        <Mono>Not published here</Mono>
        <ul className="mt-4 space-y-2 text-[15px] text-o-dim">
          <li>Complete mechanism specifications</li>
          <li>Implementation specifications and source code</li>
          <li>Controlled technical documents and the research corpus</li>
        </ul>
      </div>
    </Container>
  </section>
);

export default function OrdoPage() {
  return (
    <>
      <PageIntro routeKey="ordo" group="ordo" index="GO" eyebrow="General Ordo · Governing architecture" title="General Ordo" lede={ORDO_STATEMENT}
        aside={<Placeholder label="General Ordo architectural illustration" note="The architecture shown as a whole" ratio="4 / 3" testid="placeholder-ordo-architecture" />}>
        <div className="mt-10 flex flex-wrap gap-3">
          <ActionAnchor href="#explorer" testid="ordo-explore-btn">Explore the architecture</ActionAnchor>
          <ActionLink variant="line" to="/ordo/infrastructure" testid="ordo-infra-btn">Infrastructure licensing</ActionLink>
        </div>
      </PageIntro>
      <Whole />
      <section id="explorer" className="border-b border-o-line" data-testid="ordo-explorer-section">
        <Container className="py-16 lg:py-24">
          <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Eyebrow index="02">Architecture explorer</Eyebrow>
              <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">Select a responsibility to see how the architecture holds together.</SectionTitle>
            </div>
            <p className="text-o-dim lg:col-span-4 lg:col-start-9">Every element can also be read as plain text, and every destination is linked directly below.</p>
          </div>
          <ArchitectureExplorer />
        </Container>
      </section>
      <Expressions />
      <Disclosure />
      <EngageBand path="technical" secondary="founding" from="/ordo" title="Examine the architecture more deeply."
        text="Technical collaborators and prospective partners can request an appropriately scoped examination beyond this public description." />
    </>
  );
}
